import { useEffect, useState } from "react";
import { Save, Trash2, ArrowUpRight } from "lucide-react";
import adminApi, { errorText } from "@/adminApi";

const mediaSrc = (v) => {
  if (!v) return "";
  const s = String(v);
  return s.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${s}` : s;
};

function AlbumRow({ item, categories, onSave, onDelete, busy }) {
  const [draft, setDraft] = useState(item);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(item);
    setDirty(false);
  }, [item]);

  const set = (k, v) => {
    setDraft((p) => ({ ...p, [k]: v }));
    setDirty(true);
  };

  return (
    <article className="editor-row album-row" data-testid={`album-row-${item.id}`}>
      <header className="editor-row-head">
        <strong>
          {item.media_type === "video" ? "VIDEO · " : "PHOTO · "}
          {item.title}
        </strong>
        {dirty && <span className="dirty-tag">UNSAVED CHANGES</span>}
      </header>
      <div className="album-row-body">
        {item.media_type === "video" ? (
          <video className="media-thumb" src={mediaSrc(item.url)} poster={mediaSrc(item.poster)} muted playsInline preload="metadata" />
        ) : (
          <img className="media-thumb" src={mediaSrc(item.url)} alt={item.alt || item.title} />
        )}
        <div className="editor-fields">
          <label>
            TITLE
            <input value={draft.title ?? ""} onChange={(e) => set("title", e.target.value)} data-testid={`album-title-${item.id}`} />
          </label>
          <label>
            CATEGORY
            <select value={draft.category ?? "Other"} onChange={(e) => set("category", e.target.value)} data-testid={`album-category-${item.id}`}>
              {categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label>
            ORDER
            <input type="number" value={draft.order ?? 0} onChange={(e) => set("order", Number(e.target.value))} data-testid={`album-order-${item.id}`} />
          </label>
          <label className="editor-long">
            CAPTION
            <textarea value={draft.caption ?? ""} onChange={(e) => set("caption", e.target.value)} data-testid={`album-caption-${item.id}`} />
          </label>
          <label>
            ALT TEXT
            <input value={draft.alt ?? ""} onChange={(e) => set("alt", e.target.value)} data-testid={`album-alt-${item.id}`} />
          </label>
          {item.media_type === "video" && (
            <label className="editor-media-field">
              VIDEO POSTER
              <input
                value={draft.poster ?? ""}
                placeholder="Paste Google Drive image link"
                onChange={(e) => set("poster", e.target.value)}
                data-testid={`album-poster-${item.id}`}
              />
            </label>
          )}
          <label className="editor-toggle">
            <input
              type="checkbox"
              checked={draft.published !== false}
              onChange={(e) => set("published", e.target.checked)}
              data-testid={`album-published-${item.id}`}
            />
            PUBLISHED ON /ALBUM
          </label>
        </div>
      </div>
      <div className="editor-actions">
        <button
          className="gold-btn"
          disabled={busy || !dirty}
          onClick={async () => {
            await onSave(item.id, draft);
            setDirty(false);
          }}
          data-testid={`album-save-${item.id}`}
        >
          <Save size={14} /> {dirty ? "SAVE & PUBLISH" : "SAVED"}
        </button>
        <button className="delete-btn" disabled={busy} onClick={() => onDelete(item.id)} data-testid={`album-delete-${item.id}`}>
          <Trash2 size={14} />
        </button>
      </div>
    </article>
  );
}

export default function AlbumManager({ setMessage }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(["Other"]);
  const [busy, setBusy] = useState(false);
  const [urls, setUrls] = useState("");
  const [category, setCategory] = useState("Weddings");
  const [results, setResults] = useState([]);

  const refresh = async () => {
    const { data } = await adminApi.get("/admin/album", { params: { t: Date.now() } });
    setItems(data.items);
    setCategories(data.categories);
  };

  useEffect(() => {
    refresh().catch(() => {});
  }, []);

  const importAll = async () => {
    const list = urls.split("\n").map((x) => x.trim()).filter(Boolean);
    if (list.length === 0) {
      setMessage("Paste at least one Google Drive sharing link.");
      return;
    }
    setBusy(true);
    setResults(list.map((u) => ({ url: u, status: "processing" })));
    setMessage(`Importing ${list.length} item(s) from Google Drive…`);
    try {
      const { data } = await adminApi.post("/admin/album/bulk-import", { urls: list, category });
      setResults(data.results);
      setMessage(`Imported ${data.imported}, failed ${data.failed}.`);
      if (data.imported) setUrls("");
      await refresh();
    } catch (e) {
      setResults([]);
      setMessage(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async (id, draft) => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/album/${id}`, {
        title: draft.title,
        caption: draft.caption,
        alt: draft.alt,
        category: draft.category,
        order: draft.order,
        published: draft.published !== false,
        ...(draft.media_type === "video" ? { poster: draft.poster || "" } : {}),
      });
      setMessage("Saved & published to /album.");
      await refresh();
    } catch (e) {
      setMessage(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this album item permanently?")) return;
    setBusy(true);
    try {
      await adminApi.delete(`/admin/album/${id}`);
      setMessage("Removed from the album.");
      await refresh();
    } catch (e) {
      setMessage(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="album-manager">
      <div className="editor-form album-import">
        <p className="muted">
          Paste one or more Google Drive links (photos or videos), one per line.
          Share each file as “Anyone with the link — Viewer”. Album media stays
          on /album and never changes the homepage.
        </p>
        <label>
          CATEGORY FOR THIS IMPORT
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="album-import-category">
            {categories.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="editor-long">
          GOOGLE DRIVE LINKS
          <textarea
            rows={5}
            value={urls}
            placeholder={"https://drive.google.com/file/d/FILE_ID/view\nhttps://drive.google.com/file/d/FILE_ID/view"}
            onChange={(e) => setUrls(e.target.value)}
            data-testid="album-import-urls"
          />
        </label>
        <button className="gold-btn" disabled={busy} onClick={importAll} data-testid="album-import-button">
          IMPORT ALL <ArrowUpRight size={15} />
        </button>
        {results.length > 0 && (
          <div className="album-import-results" data-testid="album-import-results">
            {results.map((r, i) => (
              <p key={i} className={`import-${r.status}`}>
                <strong>{r.status.toUpperCase()}</strong> {r.url}
                {r.error ? ` — ${r.error}` : ""}
              </p>
            ))}
          </div>
        )}
      </div>

      <p className="eyebrow album-count" data-testid="album-item-count">
        ALBUM MEDIA · {items.length}
      </p>
      <div className="editor-list">
        {items.map((item) => (
          <AlbumRow key={item.id} item={item} categories={categories} onSave={save} onDelete={remove} busy={busy} />
        ))}
        {items.length === 0 && <p className="muted">No album media yet — import from Google Drive above.</p>}
      </div>
    </div>
  );
}
